import type { Count } from '../lib/format'

import type { Translations } from './types'

/**
 * Mandarin Chinese in simplified characters. Typed against the Russian
 * source: every key and every function signature must match. Chinese nouns do
 * not change after a numeral (« 1 个信号 », « 5 个信号 »), so no plural helper
 * is needed; where a measure word is required it is written out. Amounts and
 * dates arrive already formatted; the dictionary supplies only the words
 * around them. Full-width punctuation separates Chinese clauses, half-width
 * spacing surrounds Latin text and figures.
 */
export const zh: Translations = {
  common: {
    all: '最大',
    back: '返回',
    cancel: '取消',
    confirm: '确认',
    copy: '复制',
    copied: '已复制',
    open: '打开',
    retry: '重试',
    save: '保存',
    close: '关闭',
    loading: '加载中',
    available: '可用',
    total: '合计',
    amount: '金额',
    network: '网络',
    asset: '资产',
    status: '状态',
    date: '日期',
    nothingYet: '暂无内容',
    somethingWentWrong: '出了点问题',
    reload: '重新加载',
    crashHint: '该页面无法显示。请重新加载应用 — 您的资金和申请不受影响。',
  },

  nav: {
    label: '主导航',
    home: '首页',
    markets: '行情',
    signals: '信号',
    trade: '交易',
    wallet: '钱包',
  },

  auth: {
    signIn: '登录',
    signInSubtitle: '登录后即可查看您的信号和余额',
    signInAction: '登录',
    signUp: '注册',
    signUpSubtitle: '只需邮箱和密码即可开始',
    signUpSubtitleInvite: '只需邮箱、密码和邀请码即可开始',
    email: '邮箱',
    emailPlaceholder: 'name@example.com',
    password: '密码',
    passwordHint: '至少 8 位，需包含字母和数字',
    passwordConfirm: '再次输入密码',
    referralCode: '邀请码',
    referralCodePlaceholder: '例如 8K3M7QPL',
    referralCodeHint: '选填 — 如果有 19CRT 用户邀请了您，请填写',
    referralCodeRequiredHint: '仅限邀请注册：请使用邀请人的链接或邀请码',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `${signals.text} 个奖励信号` : null,
        promo === null ? null : `${promo} USDT 活动奖金`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `如果该邀请码来自 19CRT 用户，首次充值不少于 ${deposit} ${asset} 后邀请即生效`
        : `如果该邀请码来自 19CRT 用户，首次充值不少于 ${deposit} ${asset} 后，您将获得${perks.join('和')}`
    },
    forgot: '忘记密码？',
    createAccount: '创建账户',
    haveAccount: '已有账户？',
    noAccount: '还没有账户？',
    backToSignIn: '返回登录',
    verifyTitle: '确认您的邮箱',
    verifySent: (email: string) => `我们已向 ${email} 发送了链接`,
    verifySentAnywhere: '我们已向您的邮箱发送了链接',
    verifyHint:
      '请打开邮件中的链接确认邮箱地址：找回密码的邮件会发送到该地址。如果没有收到，请查看垃圾邮件文件夹。',
    verifyEmailPrompt: '请输入注册账户时使用的邮箱',
    verifyResend: '重新发送邮件',
    verifyResendIn: (seconds: Count) => `${seconds.text} 秒后可再次发送`,
    verifyResent: '新的邮件正在发送中',
    verifyResentMaybe: '如果该邮箱已注册且尚未确认，新的邮件正在发送中',
    verifyChecking: '正在检查链接',
    verifyDone: '邮箱已确认',
    verifyDoneHint: '正在打开应用',
    verifyDoneSignInHint: '正在打开登录页',
    resetTitle: '重设密码',
    resetSubtitle: '输入您的邮箱，我们会发送修改密码的链接',
    resetSend: '发送链接',
    resetSent: '如果该账户存在，邮件正在发送中',
    resetSentHint:
      '我们不会透露某个邮箱是否已注册 — 否则此表单会被用来试探他人的邮箱。请查看收件箱和垃圾邮件文件夹。',
    newPassword: '新密码',
    newPasswordSubtitle: '请设置新密码。旧密码将立即失效。',
    newPasswordSubmit: '保存密码',
    requestNewLink: '重新获取链接',
    newPasswordDone: '密码已更新',
    newPasswordDoneHint: '正在打开登录页',
    signOut: '退出登录',
    totpCode: '验证码',
    totpHint: '身份验证器应用中的六位数字',
    recoveryCode: '恢复码',
    recoveryCodeHint: '开启两步验证时保存的八位字符恢复码',
    useRecoveryCode: '使用恢复码',
    useAppCode: '使用身份验证器应用',
    resetLinkInvalid: '此链接无效',
    errors: {
      invalidCredentials: '邮箱或密码不正确',
      emailTaken: '该邮箱已被注册',
      weakPassword: '密码过短',
      passwordRule: '密码必须同时包含字母和数字',
      passwordsDiffer: '两次输入的密码不一致',
      invalidEmail: '请输入 name@example.com 格式的地址',
      invalidReferralCode: '请检查邀请码',
      referralRequired: '请输入邀请码',
      emailRequired: '请输入邮箱',
      passwordRequired: '请输入密码',
      accountBlocked: '该账户已受限。请联系客服',
      rateLimited: '尝试次数过多。请稍后再试',
      totpRequired: '请输入身份验证器应用中的验证码',
      totpInvalid: '验证码不匹配。请检查手机时间并输入新的验证码',
      totpFormat: '应用验证码为六位数字，恢复码为八位字符',
      expiredToken: '该链接已过期。请重新获取',
      verifyLinkUsed: '该链接已被使用。请登录 — 如果您又申请过一次，请打开最新的邮件',
      missingToken: '该链接不完整。请从邮件中打开完整链接',
      validation: '请检查您填写的内容',
      network: '无法连接到服务器。请检查网络',
    },
  },

  home: {
    promoLabel: '活动',
    banners: [
      {
        title: '黄金信号',
        subtitle: '来自我们 Telegram 频道的代码 — 一步即可下单',
        cta: '输入代码',
      },
      {
        title: '实时行情',
        subtitle: '所有交易对的实时价格、图表和订单簿',
        cta: '查看行情',
      },
      {
        title: '几分钟完成充值',
        subtitle: '通过 TRC20 或 ERC20 充值 USDT',
        cta: '充值',
      },
    ],
    pauseAnnouncements: '暂停公告轮播',
    resumeAnnouncements: '继续公告轮播',
    announcementsLabel: '公告',
    announcements: [
      '充值前请核对资产和网络',
      '可在安全设置中管理账户保护',
      '可在资产页查看交易记录',
    ],
    totalBalance: '总余额',
    quickTrade: '快速交易',
    quickTradeHint: '现货与合约同在一个终端',
    quickActions: '快捷操作',
    signalLive: '有可用信号',
    signalNext: '下一个信号',
    signalClosesIn: '关闭倒计时',
    signalOpensAt: (time: string) => `${time} 开启`,
    signalsTitle: '黄金信号',
    allSignals: '全部信号',
    marketMoves: '市场动态',
    pair: '交易对',
    lastPrice: '价格',
    change24h: '24 小时涨跌',
    /** After the figure in the home balance's change pill: « +1.30% 24h ». */
    change24hShort: '24 小时',
    volume24h: '24 小时成交量',
    allMarkets: '全部行情',
    pricesUnavailable: '暂无价格数据 — 无法完整估算资产组合价值',
    ratesUnavailable: '行情数据暂时不可用',
    marketsUnavailable: '行情列表暂时不可用',
    signalsNone: '当前没有信号 — 新代码会在我们的 Telegram 频道发布',
  },

  markets: {
    title: '行情',
    search: '搜索交易对',
    spot: '现货',
    futures: '合约',
    gainers: '涨幅榜',
    losers: '跌幅榜',
    volume: '24 小时成交量',
    volumeShort: '成交量',
    typeLabel: '市场类型',
    sortLabel: '排序方式',
    favourites: '自选',
    addFavourite: '加入自选',
    removeFavourite: '取消自选',
    emptyFavourites: '暂无自选 — 给关注的交易对点星标',
    clearSearch: '清除搜索',
    empty: '没有匹配结果',
    emptyHint: '请检查交易对代码，例如 BTC 或 SOL。',
    emptySpot: '暂无开放的现货交易对',
    emptyFutures: '暂无开放的合约交易对',
    loadFailed: '行情列表加载失败',
    pricesUnavailable: '行情暂时不可用。价格会自动刷新。',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: '黄金' } as Record<string, string>,
  },

  wallet: {
    title: '钱包',
    totalValue: '账户总价值',
    accounts: '我的账户',
    latestWithdrawal: '最近一笔提币',
    coin: '币种',
    searchCoin: '搜索币种',
    quickActions: '快捷操作',
    coins: '币种',
    hideBalance: '隐藏余额',
    showBalance: '显示余额',
    loadError: '余额加载失败',
    pricesUnavailable: '暂无价格数据 — 无法完整估算资产组合价值',
    coinsEmpty: '暂无币种。完成充值后会显示在这里。',
    accountSpot: '现货账户',
    accountSpotHint: '可用于交易',
    accountFutures: '合约账户',
    accountFuturesHint: '为持仓提供的保证金',
    accountCopy: '跟单交易（归档）',
    accountCopyHint: '转入现货账户',
    actions: {
      deposit: '充值',
      withdraw: '提币',
      transfer: '划转',
      history: '记录',
    },
    deposit: {
      title: '充值',
      history: '充值记录',
      chain: '网络',
      saveQr: '保存二维码',
      address: '充值地址',
      copyAddress: '复制地址',
      notice: '重要提示',
      noticeWrongNetwork: (asset: string, network: string) =>
        `仅可在 ${network} 网络上发送 ${asset}。通过其他网络发送的资产将无法找回。`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `网络确认 ${count.text} 次后到账，通常约需 ${minutes.text} 分钟。`,
      noticeMinimum: (amount: string, asset: string) =>
        `最低充值金额为 ${amount} ${asset}。`,
      waiting: '等待您的转账',
      incoming: '到账中的充值',
      incomingFirst: '已在链上发现，等待首次确认',
      incomingProgress: (done: Count, total: Count) => `确认数：${done.text} / ${total.text}`,
      selectAsset: '选择币种',
      noAssets: '充值暂时不可用。请稍后再试。',
      noNetworks: '该币种暂无可用网络 — 请选择其他币种。',
      addressFailed: '获取充值地址失败',
      noticePermanent: '该地址是固定地址：今后每次充值都可使用。',
      qrSaved: '二维码已保存',
      qrFailed: '保存二维码失败',
      paymentLink: '钱包链接',
      paymentLinkHint: '在您的钱包中打开 — 网络和地址已为您填好。',
      copyLink: '复制链接',
    },
    withdraw: {
      title: '提币',
      history: '提币记录',
      asset: '提币资产',
      selectAsset: '选择资产',
      network: '提币网络',
      address: '提币地址',
      addressPlaceholder: (network: string) => `请输入 ${network} 网络的地址`,
      addressPaste: '粘贴地址',
      addressFromBook: (label: string): string => `已在地址簿中确认 · ${label}`,
      paste: '粘贴',
      addressInvalid: (network: string) => `这看起来不像 ${network} 网络的地址`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `这是 ${detected} 的地址，但您选择的是 ${selected}`,
      amount: '金额',
      amountPlaceholder: '0.00',
      networkFee: '网络手续费',
      platformFee: (rate: string): string => `平台手续费（${rate}%）`,
      feeFree: '暂不收取',
      feeDynamic: '按当前 gas 价格计算',
      willReceive: '实际到账',
      notice: '重要提示',
      noticeReview: '每笔申请在发送前都会经过审核。金额会立即被冻结。',
      noticeTiming: '审核通过后 24 小时内发送。',
      noticeMinimum: (amount: string, asset: string) =>
        `最低提币金额为 ${amount} ${asset}。`,
      noticeSupport:
        '请自行核对地址：转错地址无法找回。客服按申请编号处理纠纷。',
      insufficient: '余额不足',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `今日剩余额度：${remaining} / ${limit} USDT。` : `未完成认证的账户每日最多可提 ${limit} USDT，剩余 ${remaining}。完成身份认证可提高额度。`,
      addressNotConfirmed: '只能提币到地址簿中已确认的地址',
      limitReached: (verified: boolean): string => (verified ? '已达每日提币额度上限' : '已达未认证账户的每日额度上限 — 请在个人资料中完成身份认证'),
      belowMinimum: (amount: string, asset: string) => `最低 ${amount} ${asset}`,
      confirmTitle: '请核对申请',
      confirmSubtitle: '提币一旦发出便无法撤销。请核对网络和地址。',
      confirmAction: '确认提币',
      failed: '申请创建失败',
      submitted: '申请已创建',
      submittedHint: '审核通过后资金将发送至链上。可在提币申请中跟踪状态。',
      requestId: '申请编号',
      statusPending: '审核中',
      toRequests: '前往提币申请',
      empty: '当前没有可提币的资产',
      loadFailed: '资产列表加载失败',
      routeUnavailable: '该网络当前无法提币 — 请选择其他网络',
      invalidAmount: '该金额无法处理：请检查精度和网络手续费',
      edit: '修改申请',
    },
    withdrawals: {
      title: '提币申请',
      toAddress: (address: string): string => `至 ${address}`,
      note: '被拒绝或失败的申请会把冻结金额退回您的账户。打开申请可查看交易或原因。',
      empty: '暂无提币申请。每笔申请的状态都会显示在这里，从审核到链上确认。',
      loadError: '申请加载失败',
      details: '提币申请',
      created: '创建时间',
      transaction: '交易',
      openExplorer: '在区块浏览器中打开',
      reason: '原因',
      status: {
        pending: '审核中',
        approved: '已通过',
        rejected: '已拒绝',
        broadcasting: '发送中',
        sent: '已发送',
        confirmed: '已确认',
        failed: '失败',
      },
      statusHint: {
        pending: '申请正在审核中。金额已在您的账户中冻结。',
        approved: '已审核通过，等待发送至链上。',
        rejected: '申请被拒绝；冻结金额已退回您的账户。',
        broadcasting: '交易正在发送至链上。',
        sent: '交易已在链上，等待确认。',
        confirmed: '链上已确认该交易。资金已到账。',
        failed: '发送失败；冻结金额已退回您的账户。',
      },
    },
    transfer: {
      title: '划转',
      from: '从',
      fromAvailable: (amount: string): string => `从 · 可用 ${amount}`,
      to: '至',
      submit: '划转',
      swap: '交换两个账户',
      selectFrom: '转出账户',
      selectTo: '转入账户',
      selectAsset: '选择币种',
      after: '划转后',
      insufficient: '转出账户余额不足',
      done: '划转完成',
      hint: '在您自己的账户之间划转即时到账且免手续费。',
      empty: '暂无可划转的资产 — 请先充值。',
      failed: '划转失败。请重试',
    },
    history: {
      title: '交易记录',
      deposit: '充值',
      withdrawal: '提币',
      trade: '交易',
      fee: '手续费',
      transfer: '划转',
      pnl: '结果',
      empty: '暂无记录。充值、提币和交易都会显示在这里。',
      emptyFiltered: '没有符合该筛选条件的记录',
      loadError: '记录加载失败',
      today: '今天',
      yesterday: '昨天',
      balanceAfter: (amount: string) => `余额 ${amount}`,
      onlyAccount: (account: string) => `仅${account}`,
      resetAccount: '显示全部账户',
      accountFilter: '账户',
      allAccounts: '全部账户',
      withdrawalsLink: '提币申请状态',
      badges: {
        credited: '已到账',
        sent: '已发送',
        fee: '手续费',
        reserved: '已冻结',
        returned: '已退回',
        released: '已释放',
        filled: '已成交',
        settled: '已结算',
        liquidated: '已强平',
        done: '已完成',
        adjusted: '已调整',
        paid: '已支付',
      },
      filters: {
        all: '全部',
        rewards: '奖励',
        deposits: '充值',
        withdrawals: '提币',
        trades: '交易',
        transfers: '划转',
      },
      types: {
        deposit: '充值',
        withdrawal: '提币',
        withdrawal_fee: '提币手续费',
        withdrawal_platform_fee: '提币平台手续费',
        withdrawal_lock: '提币冻结',
        withdrawal_release: '提币冻结退回',
        order_lock: '限价单冻结',
        order_release: '订单冻结退回',
        bonus: '活动奖金',
        trade_buy: '买入',
        trade_sell: '卖出',
        trading_fee: '交易手续费',
        realized_pnl: '交易结果',
        signal_programmed_profit: '信号预设收益',
        funding: '资金费用',
        margin_lock: '保证金冻结',
        margin_release: '保证金释放',
        liquidation: '强制平仓',
        transfer_in: '划转转入',
        transfer_out: '划转转出',
        copy_allocation: '划入跟单',
        copy_release: '从跟单退回',
        copy_commission: '跟单手续费',
        referral: '推广奖励',
        adjustment: '账目调整',
      },
    },
  },

  trade: {
    title: '交易',
    menu: {
      account: '账户',
      trading: '交易',
      more: '更多',
    },
    long: '做多',
    short: '做空',
    buy: '买入',
    sell: '卖出',
    marketOrder: '市价',
    limitOrder: '限价',
    price: '价格',
    marketPrice: '按市价',
    quantity: '数量',
    leverage: '杠杆',
    takeProfitStopLoss: '止盈 / 止损',
    positions: '持仓',
    orders: '订单',
    history: '记录',
    copyTrading: '跟单交易',
    unrealizedPnl: '未实现盈亏',
    unrealizedPnlShort: '盈亏',
    margin: '保证金',
    entry: '开仓价',
    size: '仓位',
    liquidation: '强平价',
    close: '平仓',
    orderBook: '订单簿',
    noPositions: '在上方下单后，持仓会显示在这里。',
    balance: '余额',
    perpetual: '永续',
    perpShort: '永续',
    fundingIn: (time: string) => `${time}后`,
    position: '持仓',
    closeAtMarket: '市价平仓',
    keepPosition: '保留持仓',
    placeBuyOrder: '下买入单',
    placeSellOrder: '下卖出单',
    placeLongOrder: '下做多单',
    placeShortOrder: '下做空单',
    isolatedMargin: '逐仓保证金',
    crossMargin: '全仓保证金',
    markShort: '标记价',
    liqPrice: '强平价',
    noPositionsTitle: '暂无持仓',
    noOrdersTitle: '暂无挂单',
    noHistoryTitle: '暂无成交',
    pair: '交易对',
    selectPair: '选择交易对',
    sideLabel: '方向',
    orderType: '订单类型',
    sizeShare: '占可用余额的比例',
    quantityShort: '数量',
    takeProfit: '止盈',
    stopLoss: '止损',
    tabsLabel: '我的交易',
    openChart: '图表',
    openLong: (symbol: string) => `做多 ${symbol}`,
    openShort: (symbol: string) => `做空 ${symbol}`,
    buyAsset: (symbol: string) => `买入 ${symbol}`,
    sellAsset: (symbol: string) => `卖出 ${symbol}`,
    enterQuantity: '请输入数量',
    insufficient: '该账户余额不足',
    belowMinNotional: (amount: string, asset: string) =>
      `最小下单金额为 ${amount} ${asset}`,
    bookEmpty: '暂无挂单',
    bookUnavailable: '订单簿不可用',
    tradingUnavailable:
      '交易服务无响应，当前无法下单。余额、充值和提币不受影响。',
    marketsFailed: '交易对列表加载失败',
    noMarkets: '暂无交易对 — 市场开放后会显示在这里。',
    confirmTitle: '请核对订单',
    confirmSubtitle: '已成交的订单无法撤销。',
    marginRequired: '占用保证金',
    cost: '合计',
    liquidationEstimate: '预估强平价',
    submitted: '订单已提交',
    submitFailed: '下单失败',
    futuresSoon: '合约暂时不可用。现货交易正常。',
    marginMode: '保证金模式',
    cross: '全仓',
    isolated: '逐仓',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? '全仓：整个合约账户余额为该持仓提供保证'
        : '逐仓：仅该持仓的保证金承担风险',
    reduceOnly: '只减仓',
    futuresAccount: '合约账户',
    equity: '权益',
    marginUsed: '已用保证金',
    marginRatio: '风险水平',
    funding: '资金费用',
    fundingRate: '8 小时费率',
    nextFunding: '下次',
    fundingPaid: '资金费用',
    protection: '保护设置',
    editProtection: '止盈 / 止损',
    protectionSheetTitle: '止盈与止损',
    protectionSheetSubtitle: '价格一旦触及设定水平，系统会按市价平仓。',
    clearLevel: '不设置',
    addMargin: '追加保证金',
    removeMargin: '减少保证金',
    marginDelta: '保证金金额',
    marginDeltaHint: '逐仓持仓：保证金越多，强平价越远',
    addMarginKyc: '证件审核通过后即可追加保证金。目前您仍可减少保证金、修改止盈止损或平仓。',
    saveProtection: '保存',
    protectionSaved: '保护设置已更新',
    protectionFailed: '更新持仓失败',
    protectionInvalid: '该水平位于开仓价的错误一侧',
    leverageTooHigh: '杠杆超过该市场的上限',
    leverageTier: (cap: number, notional: string) => `${notional} USDT 仓位最高 ${cap}×`,
    futuresDisabled: '当前不接受合约订单',
    liquidated: '已强平',
    restingToast: (symbol: string, price: string) => `限价单 ${symbol} ${price} 已挂出，正在等待价格`,
    cancelledToast: '订单已取消，冻结资金已释放',
    costSell: '实际到账',
    costBasis: '成本',
    enterProtection: '止盈或止损水平不是有效价格',
    leverageLocked: (leverage: number) => `当前持仓将杠杆固定为 ${leverage}×。平仓后方可修改`,
    marginReleased: '保证金已释放',
    realizedEstimate: '预估结果',
    addsPosition: (quantity: string, asset: string) => `为现有持仓增加 ${quantity} ${asset}`,
    reducesPosition: (quantity: string, asset: string) => `将现有持仓减少 ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `平掉现有的 ${quantity} ${asset} 持仓`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `平掉 ${closeQuantity} ${asset} 持仓，并反向开立 ${openQuantity} ${asset}`,
    byAccount: '整个账户',
    cancelledToastPlain: '订单已取消',
    fillBuy: '买入',
    fillSell: '卖出',
    marginFloor: (floor: string) => `保证金不得低于初始保证金 ${floor} USDT`,
    marginRemovable: (amount: string) => `最多可减少 ${amount} USDT`,
    enterPrice: '请输入限价',
    markPrice: '标记价格',
    tpShort: '止盈',
    slShort: '止损',
    priceStale: '价格数据过旧 — 行情推送有延迟。请几秒后重试。',
    marketDisabled: '该交易对已暂停交易',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `${side} ${quantity} ${asset}，成交价 ${price} — 已成交`,
    closedToast: (symbol: string, pnl: string) => `${symbol} 已平仓，结果 ${pnl} USDT`,
    positionsFailed: '持仓加载失败',
    noOrders: '限价单会在这里等待成交。',
    ordersFailed: '订单加载失败',
    noHistory: '您的第一笔成交将显示在此列表中。',
    historyFailed: '成交记录加载失败',
    cancelOrder: '取消',
    cancelFailed: '取消订单失败',
    closeFailed: '平仓失败',
    closeConfirmTitle: '确定平仓吗？',
    closeConfirmSubtitle:
      '系统将按市价平仓，结果即为最终结果。此操作无法撤销。',
  },

  chart: {
    title: '图表',
    interval: '周期',
    chartType: '图表类型',
    candles: 'K线',
    line: '折线',
    high24h: '24 小时最高',
    low24h: '24 小时最低',
    volume24h: '24 小时成交量',
    empty: '该交易对有数据后将显示图表',
    failed: '图表加载失败',
    unknownPair: '没有该交易对 — 请在行情页选择',
    toMarkets: '前往行情',
  },

  security: {
    title: '安全',
    subtitle: '两步验证保护登录和提币',
    statusOn: '已开启',
    statusOff: '未开启',
    explain:
      '登录和每一笔提币申请都会要求输入身份验证器应用中的验证码。未开启时，账户仅由密码保护。',
    setup: '设置身份验证器',
    step1: '使用 Google Authenticator、1Password 或 Aegis 扫描二维码',
    secretLabel: '或手动输入密钥',
    step2: '输入应用中的验证码',
    enable: '开启',
    enabled: '两步验证已开启',
    disableTitle: '关闭两步验证',
    disableHint: '请输入当前验证码，以证明手机在您手中',
    disable: '关闭',
    disabled: '两步验证已关闭',
    codeInvalid: '验证码不匹配。请检查手机时间并输入新的验证码',
    failed: '安全设置修改失败',
    withdrawalCode: '验证码',
    withdrawalCodeHint: '提币受两步验证保护：请输入应用中的验证码',
    recoveryTitle: '恢复码',
    recoveryIntro: '请将这些恢复码妥善保管。手机不在身边时，每个恢复码可用于一次登录或一次提币。它们不会再次显示。',
    recoveryCopy: '复制恢复码',
    recoveryCopied: '恢复码已复制',
    recoverySaved: '我已保存恢复码',
    recoveryLeft: (count: number): string => `剩余恢复码：${count}`,
    recoveryRegenerate: '生成新的恢复码',
    recoveryRegenerateHint: '旧的恢复码将失效。请输入应用中的验证码',
    recoveryRegenerated: '已生成新的恢复码',
    codeOrRecovery: '应用中的六位数字，或一个恢复码',
  },

  addressBook: {
    title: '提币地址',
    hint: '新地址需打开邮件中的链接后才会生效，这样即使会话被盗，也无法把资金转到对方的地址。',
    add: '添加地址',
    address: '地址',
    label: '备注名',
    labelPlaceholder: '例如 Ledger',
    send: '发送确认邮件',
    saved: '已保存的地址',
    empty: '暂无地址',
    emptyBody: '添加您常用的提币地址：通过邮件确认后，在提币表单中一键即可选用。',
    statusConfirmed: '已确认',
    statusPending: '等待邮件确认',
    resend: '重新发送',
    resendLink: '重新发送确认邮件',
    remove: '删除地址',
    removeTitle: '删除该地址吗？',
    removeHint: '在您重新添加并确认之前，将无法向该地址提币。',
    linkSent: '邮件已发送 — 请打开链接确认地址',
    alreadyConfirmed: '该地址已确认',
    invalid: '该地址与所选网络不匹配',
    confirmed: (short: string): string => `地址 ${short} 已确认`,
    confirmFailed: '链接无效或已过期',
  },

  sessions: {
    title: '会话与设备',
    subtitle: '您的账户在哪些设备上登录',
    current: '当前设备',
    started: '登录时间',
    lastSeen: '最近活动',
    end: '结束',
    endOthers: '结束其他所有会话',
    ended: '会话已结束',
    endedOthers: (count: number): string => `已结束的会话：${count}`,
    empty: '没有其他活动会话',
    failed: '会话加载失败',
    hint: '结束的会话会在十五分钟内失去访问权限：这是已签发访问密钥的有效期。',
  },

  profile: {
    title: '个人资料',
    accountId: '账户 ID',
    idCopied: 'ID 已复制',
    loadFailed: '个人资料加载失败',
    partnerProgram: '推广合作计划',
    partnerProgramHint: '团队、等级与奖励',

    verification: '身份认证',
    verificationNone: '未开始',
    verificationPending: '审核中',
    verificationInReview: '审核中',
    verificationApproved: '已认证',
    verificationRejected: '未通过',
    verificationResubmit: '需要新的材料',
    verificationCta: '开始',
    verificationCtaAgain: '重新提交',
    verificationPrompt: '上传您的证件 — 只需两分钟',
    verificationPendingHint: '工作人员通常会在一天内完成审核。',
    verificationRejectedHint: '工作人员未接受这些材料。您可以重新提交。',
    verificationOpen: '打开申请',

    withdrawalAddressesHint: '地址簿，通过邮件确认',
    withdrawalAddresses: '提币地址',
    addressesSubtitle: '您曾提币到过的地址',
    addressesEmpty: '您还没有提过币。首次申请中的地址会显示在这里。',
    addressesFailed: '地址加载失败',
    addressCopied: '地址已复制',

    security: '安全',
    accountAccess: '邮箱和密码',
    sessions: '会话与设备',
    sessionsHint: '您在哪些设备上登录',
    securityOn: '两步验证已开启',
    securityOff: '设置两步验证',
    securitySubtitle: '账户访问',
    securityEmailVerified: '邮箱已确认',
    securityEmailUnverified: '邮箱未确认',
    securityPassword: '修改密码',
    securityPasswordHint: '我们会通过邮件发送设置新密码的链接',

    notifications: '通知',
    pushOn: '推送已开启',
    pushOff: '推送已关闭',
    pushToggle: '推送通知',
    pushUnsupported: '该浏览器不支持推送通知',
    pushDenied: '浏览器设置中已阻止通知',
    pushUnavailable: '服务器尚未配置推送通知',

    pushFailed: '开启推送通知失败',

    theme: '主题',
    themeHint: '黑色是品牌色，在 OLED 屏幕上更省电。浅色适合强光环境。「跟随系统」会跟随手机设置。',
    themeNames: { system: '跟随系统', dark: '深色', light: '浅色' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: '跟随设备设置',
    language: '语言',
    languageHint: '界面语言。金额格式始终统一，日期则跟随语言。',

    support: '客服',
    supportTitle: '联系客服',
    supportChoose: '选择您方便的方式',
    supportVia: 'Telegram 或邮箱',
    supportTelegram: '通过 Telegram 联系',
    supportEmail: '发送邮件',
    about: '关于',
    version: '版本',

    signOut: '退出登录',
    signOutTitle: '退出该账户吗？',
    signOutBody:
      '余额、未平仓位和信号记录都会原样保留，下次登录即可继续。其他设备仍保持登录状态。',

    kyc: {
      title: '身份认证',
      intro:
        '仅审核人员可以看到这些信息。证件号码加密存储，且不会完整显示。',
      loadFailed: '申请加载失败',

      document: '证件',
      documentType: '证件类型',
      typePassport: '护照',
      typeIdCard: '身份证',
      typeDriverLicense: '驾驶证',
      number: '证件号码',
      numberPlaceholder: '4510 123456',
      numberHint: '按证件上印刷的内容填写，包括字母与数字',
      country: '签发国家',
      countryPlaceholder: '选择国家',
      issueDate: '签发日期',
      expiryDate: '有效期至',

      personal: '证件持有人',
      lastName: '姓',
      firstName: '名',
      dateOfBirth: '出生日期',

      documents: '证件照片',
      docMain: '主页',
      docMainHint: '含照片和号码的那一页',
      docFront: '正面',
      docFrontHint: '含照片和号码的一面',
      choose: '选择文件',
      replace: '更换',
      remove: '删除',
      fileHint: (size: string) => `JPG、PNG 或 PDF，不超过 ${size} MB`,
      megabytes: 'MB',

      submit: '提交审核',
      submitFailed: '申请提交失败。请检查填写内容后重试',
      uploadFailed:
        '资料已提交，但文件未能上传。请重新尝试添加附件。',
      uploadRetry: '上传证件',
      submitted: '申请已提交',
      submittedHint: '工作人员将审核材料并更新状态。',

      review: '申请审核中',
      reviewHint: '审核期间无法修改资料。',
      approved: '身份认证已完成',
      approvedHint: '工作人员已确认您的证件。',
      rejected: '申请未通过',
      rejectedHint: '请修改资料后重新提交申请。',
      resubmit: '需要新的材料',
      resubmitHint: '请上传在有效期内的证件并重新提交申请。',
      rejectionReason: '原因',
      submittedAt: (date: string) => `提交于 ${date}`,
      attached: '已附文件',
      attachedEmpty: '该申请没有附件',
      startNew: '重新提交',
      step: (current: number, total: number): string => `第 ${current} 步，共 ${total} 步`,

      gate: {

        title: '完成身份认证后即可使用资金',

        body: '您可以立即充值。交易、信号、划转和提币将在证件审核通过后开放，通常需要一天。',

        pending: '您的证件正在审核中 — 通过后即可开放，通常需要一天。',

        action: '去认证',

      },

      kinds: {
        passport_main: '主页',
        passport_registration: '住址登记页',
        selfie_with_document: '手持证件自拍',
        proof_of_address: '地址证明',
      },

      errors: {
        required: '请填写此项',
        number: '仅限字母、数字、空格和连字符',
        name: '仅限字母、空格、撇号和连字符',
        country: '请选择国家',
        date: '请选择日期',
        issueFuture: '签发日期不能是未来的日期',
        expiryPast: '该证件已过期',
        expiryBeforeIssue: '有效期截止日必须晚于签发日期',
        age: '年满 18 周岁方可进行身份认证',
        birthAfterIssue: '出生日期必须早于签发日期',
        fileType: '仅支持 JPG、PNG 或 PDF',
        fileSize: (size: string) => `文件超过 ${size} MB`,
        documentsRequired: '请附上证件的照片或扫描件',
        fileMissing: '请添加该文件',
      },
    },
  },

  countries: {
    search: '国家名称或代码',
    popular: '常用',
    all: '所有国家',
    nothing: '没有找到。请试试英文名称或代码，例如 DE',
    found: (count: number): string => `找到国家：${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `${amount} USDT 活动奖金已解锁并计入现货账户`,
    promoExpired: '活动奖金已过期：未在期限内达成条件',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `您邀请的 ${name} 已达标：+${signals.text} 个奖励信号`
        : `您邀请的 ${name} 已达标`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} 个奖励信号` : null,
        promo === null ? null : `${promo} USDT 活动奖金已锁定，达成条件后解锁`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? '您的推广达标已完成' : `已达标：${perks.join('，')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? '达标资格已撤销：在锁定期内提交了提币申请。奖励信号和活动奖金已收回'
          : '达标资格已撤销：奖励信号和活动奖金已收回'
        : `您邀请的 ${name} 的达标资格已撤销：因他获得的奖励信号已收回`,
    referralRewardPaid: (amount: string): string => `推广合作计划已支付 ${amount} USDT`,
    referralRewardRejected: (amount: string): string => `${amount} USDT 的奖励被工作人员拒绝`,
    partnerLevelReached: (level: string, amount: string): string =>
      `已达到 ${level}：${amount} USDT 奖金正在等待发放`,
    title: '通知',
    settingsTitle: '通知设置',
    settingsHint: '关闭的类别仍会保留在历史记录中，但不会提醒您：没有小红点，也没有推送。推送需在个人资料中开启后才生效。',
    channelInApp: '应用内',
    channelPush: '推送',
    channelTelegram: 'Telegram',
    telegramHint: '机器人会发送到您对话中的内容。在此关闭的类别不会发到 Telegram，但仍保留在应用内。',
    telegramLinked: (name: string): string => (name === '' ? '已连接 Telegram' : `已连接 Telegram：${name}`),
    telegramLinkedHint: '如果不是您本人操作，请点击断开连接。',
    telegramUnlinked: (name: string): string => (name === '' ? '已断开 Telegram' : `已断开 Telegram：${name}`),
    telegramUnlinkedHint: '该对话此前连接的是另一个账户。',
    categories: { deposit: '充值', withdrawal: '提币', copy: '跟单交易', trade: '交易与持仓', signal: '信号', promo: '活动奖金', referral: '推广合作计划', kyc: '身份认证', system: '系统' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: '全部标为已读',
    markAllFailed: '标记通知为已读失败',
    loadFailed: '通知加载失败',
    empty:
      '暂无通知。到账的充值、提币申请的结果、信号和交易结果都会显示在这里。',
    unread: '未读',
    today: '今天',
    yesterday: '昨天',
    depositCredited: (amount: string, asset: string) => `${amount} ${asset} 已到账`,
    withdrawalApproved: '提币已通过审核',
    withdrawalRejected: '提币被拒绝',
    withdrawalSent: '提币已发送至链上',
    marginCall: '追加保证金通知',
    positionLiquidated: '仓位已被强平',
    copyOpened: (master: string) => `${master} 开立了新仓位`,
    copyClosed: (master: string) => `${master} 平掉了仓位`,
    copyStopLoss: '订阅止损已触发：仓位已平，资金已退回现货账户',
    copyMasterStopped: '交易员已停止：跟单结束，资金已退回现货账户',
    copyReleased: (amount: string) => `已退回现货账户：${amount} USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `支付给交易员的利润分成：${profit} USDT 利润中的 ${commission} USDT` : `支付给 ${master} 的利润分成：${profit} USDT 利润中的 ${commission} USDT`,
    signalLive: (title: string, until: string) => `信号「${title}」开放至 ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `${symbol} 仓位已在 ${price} 被强平`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}：已在 ${price} 触发止盈`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}：已在 ${price} 触发止损`,
    orderFilled: (symbol: string, quantity: string, price: string) => `限价单 ${symbol} 已成交：${quantity}，成交价 ${price}`,
    orderRejected: (symbol: string) => `限价单 ${symbol} 未成交：系统拒绝了该笔交易`,
    orderRejectedFunds: (symbol: string) => `限价单 ${symbol} 未成交：合约账户保证金不足`,
    orderRejectedLeverage: (symbol: string) => `限价单 ${symbol} 未成交：杠杆超过该仓位规模的上限`,
    kycApproved: '身份认证已通过',
    kycRejected: '身份认证未通过',
    kycResubmit: '身份认证需要新的材料',
    kycExpiring: (date: string) => `您的证件将于 ${date} 到期 — 请更新身份认证`,
    kycExpired: '您的证件已过期 — 请上传有效证件',
    signalClosed: (title: string) => `信号已关闭：${title}`,
    systemAlert: (check: string) => `故障：${check}`,
    systemRecovered: (check: string) => `已恢复：${check}`,
    heads: {
      signalLive: '信号已开启',
      signalClosed: '信号已关闭',
      orderFilled: '限价单已成交',
      orderRejected: '限价单未成交',
      takeProfit: '已触发止盈',
      stopLoss: '已触发止损',
      promoUnlocked: '活动奖金已解锁',
      promoExpired: '活动奖金已过期',
      referralQualified: '受邀用户已达标',
      referralWelcome: '已达标',
      referralRevoked: '达标资格已撤销',
      rewardPaid: '奖励已支付',
      rewardRejected: '奖励被拒绝',
      partnerLevelReached: '新的合作等级',
      kycExpiring: '证件即将到期',
      kycExpired: '证件已过期',
      systemAlert: '故障',
      systemRecovered: '已恢复',
    },
    fallback: {
      deposit: '充值已到账',
      withdrawal: '提币申请有更新',
      trade: '持仓事件',
      copy: '跟单交易事件',
      referral: '推广合作计划事件',
      system: '系统通知',
    },
  },

  referrals: {
    title: '推广合作计划',
    rewardKind: {
      fee_rebate: '手续费分成',
      signal_grant: '奖励信号',
      promotional_credit: '活动奖金',
      deposit_bonus: '充值分成',
      welcome_bonus: '新人分成',
      level_bonus: '等级奖金',
    },
    rewardStatus: {
      pending_review: '审核中',
      approved: '已通过',
      paid: '已支付',
      rejected: '已拒绝',
      reversed: '已撤回',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: '已锁定',
      unlocked: '已到账',
      expired: '已过期',
      revoked: '已撤销',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: '会员',
      bronze: '青铜',
      silver: '白银',
      gold: '黄金',
      platinum: '铂金',
      ambassador: '大使',
    } as Record<string, string>,
    signalUnit: '个信号',
    membersFrom: (count: Count): string => `${count.text} 人起`,
    baseLevel: '起始等级',
    depositFrom: (amount: string): string => `充值 ${amount} 起`,
    noWeeklySignals: '无信号',
    perWeek: (count: Count): string => `每周 ${count.text} 个信号`,
    current: '当前',
    copyFailed: '复制失败',
    inviteText: '用我的邀请码加入 19CRT',
    linkCopied: '链接已复制',
    codeCopied: '邀请码已复制',
    loadError: '推广合作计划加载失败',
    loadErrorHint: '请检查网络连接后重试',
    retry: '重试',
    disabled: '推广合作计划已暂停。您的数据和奖励都已保留。',
    yourCode: '您的邀请码',
    codeHint: '直接邀请 — 所有条款在注册前即可查看',
    openQr: '打开推广二维码',
    qrCode: '二维码',
    share: '分享',
    copy: '复制',
    yourLevel: '您的等级',
    participant: '会员',
    allLevels: '全部等级',
    progressLabel: '升至下一等级的进度',
    qualified: (): string => '已达标',
    nextLevel: (remaining: Count, level: string): string => `再邀请 ${remaining.text} 人升至 ${level}`,
    maxLevel: '已达最高等级',
    invited: '已邀请',
    confirmed: '已达标',
    paidUsdt: '已支付，USDT',
    signalsReceived: '已获得信号',
    transparentTitle: '透明的奖励',
    transparentHint:
      '奖励仅基于平台实际收取的交易手续费计算。受邀用户的充值款绝不会被重新分配。',
    rewards: '奖励',
    rewardHistory: '奖励记录',
    underReview: '审核中',
    emptyRewardsTitle: '暂无奖励',
    emptyRewards: '团队首次达成条件后，奖励会显示在这里。',
    levelsRules: '等级与规则',
    levelsRulesHint: '团队、手续费分成与信号',
    inviteTitle: '邀请加入 19CRT',
    inviteSubtitle: '邀请码在注册时绑定',
    copyLink: '复制链接',
    levelsTitle: '合作等级',
    levelsSubtitle: '仅统计直接邀请且已达标的成员',
    loadLevelsError: '等级加载失败',
    levelTeam: '等级与团队',
    feeSignals: '手续费分成 · 信号',
    depositTiersTitle: '按充值金额分级的比例',
    depositTiersHint: '首次充值越多，双方获得的比例越高',
    tierDeposit: '充值金额',
    tierReferrer: '您获得',
    tierWelcome: '新人获得',
    tierFrom: (amount: string): string => `${amount} 及以上`,
    levelBonus: '等级奖金',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: '计划规则',
      qualification: (amount: string, asset: string): string =>
        `达标条件：首次到账充值单笔不少于 ${amount} ${asset}。`,
      qualificationKyc: '需要先完成身份认证。',
      welcome: (signals: Count, days: Count): string =>
        `新人可获得 ${signals.text} 个奖励信号，有效期 ${days.text} 天。`,
      referrer: (signals: Count, days: Count): string =>
        `每有一位受邀用户达标，推广人即可获得 ${signals.text} 个奖励信号，有效期 ${days.text} 天。`,
      promo: (amount: string, required: string, days: Count): string =>
        `${amount} USDT 活动奖金将被锁定；当新人的交易额在 ${days.text} 天内达到 ${required} USDT 时，奖金将变为现货账户中的资金，否则作废。`,
      hold: (days: Count): string =>
        `达标后 ${days.text} 天内提交任何提币申请，都会使达标资格连同奖励信号和活动奖金一并作废。`,
      feeShare:
        '等级手续费分成仅按平台实际从受邀用户处收取的交易手续费计算。现金奖励由工作人员审核后单独发放，绝不会自动计入余额。',
      secondLevel: (rate: string): string => `推广人的推广人可获得同一笔手续费的 ${rate}。`,
      depositTiers:
        '推广人和新人的比例取决于首次充值的金额 — 详见本页表格。',
      balanceShare: (percent: string): string =>
        `充值金额还须不低于推广人到账时余额的 ${percent}%。`,
      topup: (days: Count): string =>
        `首次充值后 ${days.text} 天内再次充值的金额将与首次合并，按表格重新计算，差额补发一次。`,
      premiumSignal: (count: string): string =>
        `当已达标的受邀用户达到 ${count} 人或以上时，永久开通高级信号权限。`,
      antiabuse:
        '如果设备或地址与推广人或其团队重复，达标资格将转交工作人员审核。',
      paused: '工作人员已暂停该计划：不再产生新的达标资格和奖励。',
    },
    disclaimer:
      '推广奖励并非保证收益。注册、充值和交易量本身并不产生现金支付。',
    team: '团队',
    teamStatus: {
      pending: '等待充值',
      flagged: '等待工作人员审核',
      qualified: '已达标',
      revoked: '达标资格已撤销',
    },
    joined: (date: string): string => `自 ${date} 起`,
    showAll: (count: Count): string => `显示全部（${count.text}）`,
    showLess: '收起',
    promoTitle: '活动奖金',
    promoLocked: (amount: string): string => `已锁定 ${amount} USDT`,
    promoUnlocked: (amount: string): string => `${amount} USDT 已计入现货账户`,
    promoExpired: '已过期，奖金作废',
    promoRevoked: '奖金已撤销',
    promoCondition: (traded: string, required: string): string => `交易额 ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `截至 ${date}`,
    promoHint: '当您的累计交易额在截止日期前达到条件时，奖金即变为可用资金。',
  },

  signals: {
    title: '信号',
    tabs: { today: '今天', history: '历史' },
    tabsLabel: '信号分区',
    access: { public: '公开', base: '基础', premium: '高级', bonus: '奖励', agent: '合作' },
    status: { scheduled: '已排期', available: '可执行', executed: '已执行', expired: '已过期', cancelled: '已取消' },
    side: { buy: '买入', sell: '卖出' },
    direction: { buy: '做多 · 买入', sell: '做空 · 卖出' },
    positionSide: { buy: '做多', sell: '做空' },
    localTime: '本地时间',
    localClock: '本地时间',
    timeZone: (offset: string) => `时区 ${offset}`,
    reason: {
      unavailable: '该信号当前不可用',
      already: '您已执行过该信号',
      expired: '执行窗口已结束',
      notReleased: '该信号尚未开启',
      cancelled: '该信号已被取消',
      balance: '现货账户余额不足',
      balanceFutures: '现货与合约账户的 USDT 合计不足',
      positionConflict: '您在该市场有反向持仓 — 请先平仓再执行信号',
      price: '该市场当前没有价格 — 请几秒后重试',
      access: '该信号需要其他等级的访问权限',
      kyc: '请先完成身份认证 — 证件通过后即可执行信号',
      minimum: '该信号按您总余额的一定比例下单，而该比例低于最低限额。请充值后再执行',
      maximum: '金额超过该信号的上限',
      belowMinimum: (amount: string) => `该信号按您总余额的一定比例下单，而该比例低于最低限额 ${amount} USDT。请充值后再执行`,
      aboveMaximum: (amount: string) => `金额超过该信号的上限 — 最多 ${amount} USDT`,
      notFound: '未找到与该代码对应的信号',
      generic: '操作未能完成。请检查条款后重试',
      network: '无法连接服务器。请检查网络后重试',
    },
    enterCode: '输入代码',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: '输入频道中的代码，确认前即可查看方向、金额和条款', executed: '结果和实际参数见历史记录', scheduled: (time: string) => `代码将于 ${time} 在频道公布` },
    executing: '执行中',
    notExecuted: '未执行',
    executedLabel: '已执行',
    loadSignalsError: '信号加载失败',
    noSignalsToday: '今天没有信号',
    loadErrorHint: '请检查网络连接后重试',
    retry: '重试',
    signalAvailable: '有可用信号',
    nextSignal: '下一个信号',
    windowOpenUntil: (time: string) => `开放至 ${time}`,
    opensAt: (time: string) => `${time} 开启`,
    execute: '执行',
    doneToday: '今天已全部完成',
    newBySchedule: '新信号将按计划公布',
    codeOverline: '信号代码',
    checkTerms: '查看条款',
    codeHint: '每个代码都会在我们的 Telegram 频道公布 — 看到后请立即输入。',
    openChannel: '打开频道',
    channelLink: 'Telegram 频道',
    panelTitle: '用代码下单',
    dealsTitle: '按代码成交的交易',
    dealsEmpty: '暂无按代码成交的交易',
    dealsEmptyHint: '输入频道中的代码 — 交易及其结果会显示在这里',
    codePlaceholder: '例如 1QF9ET7RQ',
    codeRequired: '请输入频道中的代码',
    codeLabel: '信号代码',
    check: '查看',
    schedule: '时间表',
    todaySignals: '今天的信号',
    noSignals: '当前没有信号。新代码会按计划在我们的 Telegram 频道公布。',
    riskDisclosure:
      '信号是一种交易思路，并非保证收益。确认之前，您会看到方向、金额、杠杆、手续费和平仓条件。',
    loadHistoryError: '信号历史加载失败',
    historyEmpty: '暂无历史记录',
    historyEmptyHint: '已执行的信号及其实际参数会显示在这里。',
    toSignals: '查看信号',
    allExecutions: '全部执行记录',
    signalAccepted: '信号已接受',
    confirmation: '确认信号',
    sentToTrading: '订单已发送至交易系统',
    checkBeforeExecute: '执行前请核对条款',
    acceptedHint: '再次点击不会产生第二笔订单。',
    ready: '完成',
    account: '账户',
    spot: '现货',
    futures: '合约',
    perpetualMarket: '合约',
    validUntil: '有效期',
    signalName: '信号',
    until: (time: string) => `至 ${time}`,
    timeLeft: (countdown: string) => `剩余 ${countdown}`,
    priceGuide: '参考价格',
    entryGuide: '参考开仓价',
    marketPrice: '市价',
    stopLoss: '止损',
    takeProfit: '止盈',
    autoClose: '自动平仓',
    inDuration: (duration: string) => `${duration}后`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days} 天` : null, hours > 0 ? `${hours} 小时` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes} 分钟` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: '已执行',
    executionUnavailable: '无法执行',
    executionAmount: '执行金额',
    marginAmount: '保证金',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `您总余额的 ${share} · ${total} USDT`,
    estimatedFee: '预估手续费',
    totalDebit: '合计扣除',
    positionSize: '仓位规模',
    willSell: '将卖出',
    fundingNote: (amount: string) =>
      `本次交易将把 ${amount} USDT 从您的现货账户划转至合约账户。平仓时，该金额连同盈亏结果会退回您的现货账户。`,
    executionRisk:
      '信号是一种交易思路，并非保证收益。实际成交价可能与预估不同，交易也可能亏损。请仅确认您理解的风险。',
    executionRiskFutures:
      '信号是一种交易思路，并非保证收益。实际开仓价可能与预估不同，杠杆会同时放大盈利和亏损，甚至导致仓位被强平。请仅确认您理解的风险。',
    retryExecution: '重新执行',
    outcomeLabel: '结果，USDT',
    outcomeOpen: '持仓中',
    outcome: { take_profit: '止盈平仓', stop_loss: '止损平仓', time: '到期平仓', manual: '手动平仓', liquidation: '强平平仓' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: '已止盈', stop_loss: '已止损', time: '已到期', manual: '手动', liquidation: '已强平' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: '持仓中',
    executeFor: '最多可执行',
    openLong: (amount: string) => `开多 · ${amount} USDT`,
    openShort: (amount: string) => `开空 · ${amount} USDT`,
    submittedToast: '信号已提交执行',
    filledOf: (requested: string, filled: string) =>
      `申请 ${requested} USDT，成交 ${filled} USDT — 数量已按最小交易单位取整。`,
    marginRounded: (requested: string) => `申请 ${requested} USDT — 数量已按最小交易单位取整。`,
    longOpened: (margin: string, leverage: number) => `已开多：保证金 ${margin} USDT，杠杆 ${leverage}×。`,
    shortOpened: (margin: string, leverage: number) => `已开空：保证金 ${margin} USDT，杠杆 ${leverage}×。`,
    fundedFromSpot: (amount: string) => `已从您的现货账户划转 ${amount} USDT。`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? '止盈' : null, stopLoss ? '止损' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `将于 ${duration}后自动平仓`
      const byLevel = `触及${levels.join(duration === null ? '或' : '、')}时`
      return duration === null ? `${byLevel}平仓` : `${byLevel}平仓，或于 ${duration}后平仓`
    },
    openPosition: '前往持仓',
    returnedToSpot: (amount: string) => `已退回现货账户：${amount} USDT`,
    positionAccepted: '仓位已在您的合约账户中开立',
  },

  notFound: {
    title: '页面不存在',
    message: '这里没有这个页面 — 链接已失效或地址有误。',
    action: '返回首页',
  },
}
